import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientPortalAdminAPI } from '../api/clientPortal';

const ClientPortalEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [portal, setPortal] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reportsFolderUrl, setReportsFolderUrl] = useState('');
  const [savingReportsUrl, setSavingReportsUrl] = useState(false);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    documentType: 'pdf_report',
    title: '',
    description: '',
    externalUrl: '',
    file: null
  });

  useEffect(() => {
    fetchPortal();
  }, [id]);

  const fetchPortal = async () => {
    try {
      setLoading(true);
      const res = await clientPortalAdminAPI.get(id);
      setPortal(res.data.portal);
      setCampaigns(res.data.campaigns);
      setReportsFolderUrl(res.data.portal.reportsFolderUrl || '');
    } catch (err) {
      setError('Failed to fetch portal');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      await clientPortalAdminAPI.update(id, { isActive: !portal.isActive });
      fetchPortal();
    } catch (err) {
      setError('Failed to update portal');
    }
  };

  const handleSaveReportsFolderUrl = async () => {
    try {
      setSavingReportsUrl(true);
      await clientPortalAdminAPI.update(id, { reportsFolderUrl: reportsFolderUrl || null });
      setPortal({ ...portal, reportsFolderUrl: reportsFolderUrl || null });
    } catch (err) {
      setError('Failed to save reports folder URL');
    } finally {
      setSavingReportsUrl(false);
    }
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('Regenerate access token? The old link will stop working immediately.')) {
      return;
    }

    try {
      const res = await clientPortalAdminAPI.regenerateToken(id);
      setPortal(res.data.portal);
    } catch (err) {
      setError('Failed to regenerate token');
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/client-portal/${portal.accessToken}`;
    navigator.clipboard.writeText(link);
    alert('Link copied to clipboard!');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5MB limit');
        return;
      }
      setUploadForm({ ...uploadForm, file, title: uploadForm.title || file.name.replace(/\.[^/.]+$/, '') });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.title) {
      setError('Title is required');
      return;
    }

    if (uploadForm.documentType === 'google_sheet' && !uploadForm.externalUrl) {
      setError('URL is required for Google Sheets');
      return;
    }

    if (uploadForm.documentType !== 'google_sheet' && !uploadForm.file) {
      setError('File is required');
      return;
    }

    try {
      setUploading(true);

      let data = {
        documentType: uploadForm.documentType,
        title: uploadForm.title,
        description: uploadForm.description
      };

      if (uploadForm.documentType === 'google_sheet') {
        data.externalUrl = uploadForm.externalUrl;
      } else {
        // Convert file to base64
        const base64 = await fileToBase64(uploadForm.file);
        data.filename = uploadForm.file.name;
        data.mimeType = uploadForm.file.type;
        data.data = base64.split(',')[1]; // Remove data:... prefix
      }

      await clientPortalAdminAPI.uploadDocument(id, data);
      setShowUploadModal(false);
      setUploadForm({
        documentType: 'pdf_report',
        title: '',
        description: '',
        externalUrl: '',
        file: null
      });
      fetchPortal();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      await clientPortalAdminAPI.deleteDocument(id, docId);
      fetchPortal();
    } catch (err) {
      setError('Failed to delete document');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDocIcon = (type) => {
    const icons = {
      key_document: '📄',
      pdf_report: '📊',
      google_sheet: '📗'
    };
    return icons[type] || '📁';
  };

  const getDocTypeLabel = (type) => {
    const labels = {
      key_document: 'Key Document',
      pdf_report: 'PDF Report',
      google_sheet: 'Google Sheet'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-xl text-gray-600">Loading portal...</div>
        </div>
      </div>
    );
  }

  if (!portal) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-xl text-gray-600">Portal not found</div>
          <button
            onClick={() => navigate('/dashboard/client-portals')}
            className="mt-4 text-sc-pink hover:text-sc-pink/80"
          >
            ← Back to Portals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/client-portals')}
          className="text-gray-600 hover:text-gray-800 mb-4 inline-flex items-center"
        >
          ← Back to Portals
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{portal.clientName}</h1>
            <p className="text-gray-600">Manage portal settings and documents</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleActive}
              className={`px-3 py-1.5 text-sm rounded-md ${
                portal.isActive
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              {portal.isActive ? '● Active' : '○ Inactive'}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">×</button>
        </div>
      )}

      {/* Portal Link Section */}
      <div className="card-pink-accent bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Portal Link</h2>
        <div className="flex items-center gap-4">
          <div className="flex-1 bg-gray-50 px-4 py-3 rounded-md font-mono text-sm">
            {window.location.origin}/client-portal/{portal.accessToken}
          </div>
          <button
            onClick={copyLink}
            className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90"
          >
            📋 Copy
          </button>
          <a
            href={`/client-portal/${portal.accessToken}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            🔗 Open
          </a>
          <button
            onClick={handleRegenerateToken}
            className="px-4 py-2 border border-yellow-300 text-yellow-700 rounded-md hover:bg-yellow-50"
          >
            🔄 Regenerate
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Share this link with your client. Anyone with the link can view the portal.
        </p>
      </div>

      {/* Reports Folder URL Section */}
      <div className="card-pink-accent bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">📁 Reports Folder</h2>
        <p className="text-sm text-gray-600 mb-4">
          Add a Google Drive folder URL where reports are stored. A "Reports" button will appear in the client portal header.
        </p>
        <div className="flex items-center gap-4">
          <input
            type="url"
            value={reportsFolderUrl}
            onChange={(e) => setReportsFolderUrl(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
          />
          <button
            onClick={handleSaveReportsFolderUrl}
            disabled={savingReportsUrl || reportsFolderUrl === (portal.reportsFolderUrl || '')}
            className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {savingReportsUrl ? 'Saving...' : 'Save'}
          </button>
        </div>
        {portal.reportsFolderUrl && (
          <p className="text-xs text-green-600 mt-2">
            ✓ Reports folder is set - a "Reports" button will appear in the client portal
          </p>
        )}
      </div>

      {/* Campaigns Section */}
      <div className="card-pink-accent bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Campaigns ({campaigns.length})
        </h2>
        {campaigns.length === 0 ? (
          <p className="text-gray-500">No campaigns found for this client.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns.map(campaign => (
              <div key={campaign.id} className="border rounded-lg p-4">
                <h3 className="font-medium text-gray-900">{campaign.name}</h3>
                <div className="text-sm text-gray-500 mt-1">
                  {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
                </div>
                <span className={`mt-2 inline-block px-2 py-0.5 text-xs rounded-full ${
                  campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                  campaign.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {campaign.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents Section */}
      <div className="card-pink-accent bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Documents ({portal.documents?.length || 0})
          </h2>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 text-sm"
          >
            + Add Document
          </button>
        </div>

        {(!portal.documents || portal.documents.length === 0) ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-4xl mb-2">📁</div>
            <p className="text-gray-500 mb-4">No documents yet</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="text-sc-pink hover:text-sc-pink/80 font-medium"
            >
              Upload your first document
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {portal.documents.map(doc => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getDocIcon(doc.documentType)}</span>
                  <div>
                    <h3 className="font-medium text-gray-900">{doc.title}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                        {getDocTypeLabel(doc.documentType)}
                      </span>
                      {doc.externalUrl ? (
                        <span>🔗 External link</span>
                      ) : (
                        <span>📎 {doc.filename}</span>
                      )}
                      <span>• {formatDate(doc.createdAt)}</span>
                    </div>
                    {doc.description && (
                      <p className="text-sm text-gray-500 mt-1">{doc.description}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteDocument(doc.id)}
                  className="text-red-600 hover:text-red-800 p-2"
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Add Document</h2>

            {/* Document Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type
              </label>
              <select
                value={uploadForm.documentType}
                onChange={(e) => setUploadForm({ ...uploadForm, documentType: e.target.value, file: null, externalUrl: '' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              >
                <option value="pdf_report">PDF Report</option>
                <option value="key_document">Key Document</option>
                <option value="google_sheet">Google Sheet / External Link</option>
              </select>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="e.g., Q4 2024 Campaign Report"
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (optional)
              </label>
              <textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Brief description of the document"
              />
            </div>

            {/* File Upload or URL */}
            {uploadForm.documentType === 'google_sheet' ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL *
                </label>
                <input
                  type="url"
                  value={uploadForm.externalUrl}
                  onChange={(e) => setUploadForm({ ...uploadForm, externalUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                />
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File *
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-sc-pink transition-colors"
                >
                  {uploadForm.file ? (
                    <div>
                      <div className="text-2xl mb-2">📄</div>
                      <p className="font-medium">{uploadForm.file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="text-2xl mb-2">📁</div>
                      <p className="text-gray-600">Click to select a file</p>
                      <p className="text-xs text-gray-400 mt-1">Max 5MB • PDF, DOC, XLS, PPT</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadForm({
                    documentType: 'pdf_report',
                    title: '',
                    description: '',
                    externalUrl: '',
                    file: null
                  });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadForm.title}
                className="flex-1 px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 disabled:bg-gray-400"
              >
                {uploading ? 'Uploading...' : 'Add Document'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalEditor;
